import { http, URL } from "./http.service";
import { errorHandler } from "./error.service";

const STORAGE_KEY = 'productShortLinks';

export const createShortLink = async ({ businessUuid, businessSlug, productId, token = null }) => {
  try {
    if (token) {
      try {
        const resp = await http.post(
          `${URL}/shortlink/create`,
          {
            businessUuid,
            businessSlug,
            productId
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (resp.data?.success && resp.data?.data) {
          const { shortUrl } = resp.data.data;
          
          const shortCode = shortUrl.split('/').pop();
          storeShortLinkLocally(shortCode, {
            businessUuid,
            businessSlug,
            productId,
            fullUrl: getFullProductUrl(businessSlug, businessUuid, productId),
            createdAt: new Date().toISOString()
          });

          return shortUrl;
        }
      } catch (apiError) {
        console.log('API short link creation failed:', apiError.response?.data || apiError.message);
      }
    }

    return createLocalShortLink({ businessUuid, businessSlug, productId });
  } catch (error) {
    console.error("Error creating short link:", error);
    errorHandler(error);
    throw error;
  }
};


export const resolveShortLink = async (shortCode, businessSlug) => {
  try {
    const resp = await http.get(`${URL}/shortlink/resolve/${shortCode}`, {
      params: { businessSlug }
    });
    
    if (resp.data?.success && resp.data?.data) {
      return resp.data.data;
    }
    
    return resolveLocalShortLink(shortCode, businessSlug);
  } catch (error) {
    console.error("Error resolving short link:", error);
    
    const localResult = resolveLocalShortLink(shortCode, businessSlug);
    if (localResult) {
      return localResult;
    }
    
    errorHandler(error);
    throw error;
  }
};


export const getShortLinkStats = async (shortCode) => {
  try {
    const resp = await http.get(`${URL}/shortlink/stats/${shortCode}`);
    return resp.data;
  } catch (error) {
    console.error("Error getting short link stats:", error);
    errorHandler(error);
    throw error;
  }
};

export const deactivateShortLink = async (shortCode, token) => {
  try {
    const resp = await http.delete(`${URL}/shortlink/${shortCode}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    removeShortLinkLocally(shortCode);
    
    return resp.data;
  } catch (error) {
    console.error("Error deactivating short link:", error);
    errorHandler(error);
    throw error;
  }
};


export const getProductShareUrl = (businessSlug, businessUuid, productId) => {
  const formattedSlug = businessSlug.toLowerCase().replace(/\s+/g, '-');
  return `${window.location.origin}/${formattedSlug}/store/${businessUuid}/product/${productId}`;
};

// ============== LOCAL STORAGE HELPERS ==============

const createLocalShortLink = ({ businessUuid, businessSlug, productId }) => {
  const storedLinks = getStoredLinks();
  
  const existingEntry = Object.entries(storedLinks).find(
    ([_, data]) => data.productId === productId && data.businessUuid === businessUuid
  );

  if (existingEntry) {
    const [existingCode] = existingEntry;
    return `${window.location.origin}/${businessSlug}/p/${existingCode}`;
  }

  const productCode = productId.slice(-6);
  const uniqueCode = `${productCode}${Math.random().toString(36).substring(2, 5)}`;
  const shortUrl = `${window.location.origin}/${businessSlug}/p/${uniqueCode}`;

  storeShortLinkLocally(uniqueCode, {
    businessUuid,
    businessSlug,
    productId,
    fullUrl: getFullProductUrl(businessSlug, businessUuid, productId),
    createdAt: new Date().toISOString()
  });

  return shortUrl;
};


const resolveLocalShortLink = (shortCode, businessSlug) => {
  try {
    const storedLinks = getStoredLinks();
    const linkData = storedLinks[shortCode];
    
    if (linkData && linkData.businessSlug === businessSlug) {
      return {
        businessUuid: linkData.businessUuid,
        productId: linkData.productId
      };
    }
    
    return null;
  } catch (error) {
    console.error("Error resolving local short link:", error);
    return null;
  }
};


const getFullProductUrl = (businessSlug, businessUuid, productId) => {
  const formattedSlug = businessSlug.toLowerCase().replace(/\s+/g, '-');
  return `${window.location.origin}/${formattedSlug}/store/${businessUuid}/product/${productId}`;
};

const getStoredLinks = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};


const storeShortLinkLocally = (shortCode, data) => {
  try {
    const storedLinks = getStoredLinks();
    storedLinks[shortCode] = data;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storedLinks));
  } catch (error) {
    console.error("Error storing short link locally:", error);
  }
};


const removeShortLinkLocally = (shortCode) => {
  try {
    const storedLinks = getStoredLinks();
    delete storedLinks[shortCode];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storedLinks));
  } catch (error) {
    console.error("Error removing short link locally:", error);
  }
};